import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import { probeGetFile } from 'js/actions/probe';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { PROBE_GET_FILE_REQUEST } from 'js/constants/actionTypes';
import { IconButton } from 'new/elements/IconButton/IconButton';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectProbeByCredentialId,
  type ProbeRootState,
} from 'new/state/probe/selectors/probeTreeSelectors';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import dialogClasses from './SaveCustomProcessor.module.scss';
import classes from './TestCustomProcessor.module.scss';

interface FilePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  credentialId: number | string | null | undefined;
  path: string | null | undefined;
}

/** Read-only dialog showing a credential's file, fetched via the probe sample API when opened. */
export default function FilePreviewDialog({
  open,
  onClose,
  credentialId,
  path,
}: FilePreviewDialogProps) {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const fileContent =
    useSelector((state: ProbeRootState) =>
      path ? selectProbeByCredentialId(state, credentialId)?.files?.[path]?.response : null,
    ) || '';
  const isLoading = useIsLoading(PROBE_GET_FILE_REQUEST);

  useEffect(() => {
    if (open && credentialId != null && path) {
      dispatch(probeGetFile(credentialId, { id: path, path }));
    }
  }, [open, credentialId, path, dispatch]);

  return (
    <Dialog
      className={classes.dialog}
      open={open}
      onClose={onClose}
      data-testid="file-preview-dialog"
    >
      <DialogTitle className={dialogClasses.dialogTitle}>
        File Preview
        <IconButton onClick={onClose} data-testid="file-preview-dialog-close-button">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={dialogClasses.dialogContent} dividers>
        <Typography variant="subheading2" data-testid="file-preview-path">
          File: {path}
        </Typography>

        <div data-testid="file-preview-content" style={{ height: '100%' }}>
          <LoadableMonacoEditor
            height="100%"
            options={{
              readOnly: true,
              minimap: { enabled: false },
            }}
            value={isLoading && !fileContent ? 'Loading...' : fileContent}
            theme="dark"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
